import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import { openWindow } from "../../Slices/windowsSlice";
import { openNotesApp, closeNotesApp } from "../webtopUiSlice";
import {
  useFilteredSideMenu,
  getMenuIcon,
  subRouteOf,
  canAccessPath,
  WEBTOP_SHELL_APPS,
} from "../sideMenu";
import WebtopLayoutContext from "../layout/WebtopLayoutContext";
import {
  isHidden,
  isPinned,
  togglePin,
  hideIcon,
  getIconPosition,
  setIconPosition,
  defaultPositionForIndex,
  getShortcuts,
  removeShortcut,
  setShortcutPosition,
  addNote,
  makeNote,
} from "../layout/layoutModel";
import FolderPopover from "./FolderPopover";
import ContextMenu from "./ContextMenu";
import PersonalizePanel from "./PersonalizePanel";
import StickyNotesPanel from "./StickyNotesPanel";

const DRAG_THRESHOLD = 5;
// Windows-style desktop: a single click selects an icon, a double click opens it.
const DOUBLE_TAP_MS = 400;

/**
 * DesktopIcons — the customizable Windows 11 desktop surface (Phase 8).
 *
 * Reads the per-user layout from WebtopLayoutContext and lets the user:
 *  - drag icons to arrange them (positions persist)
 *  - right-click an icon → Pin/Unpin to taskbar, Remove from desktop
 *  - right-click the desktop → Personalize, Restore removed apps, Reset desktop
 *
 * Icons remain access-filtered (permissions stay the spine). All edits go
 * through updateLayout()/replaceLayout() → debounced backend autosave (Phase 7).
 */
const DesktopIcons = ({ devFallback = false }) => {
  const dispatch = useDispatch();
  const menu = useFilteredSideMenu({ devFallback });
  const { layout, updateLayout, replaceLayout } = useContext(WebtopLayoutContext);

  const [openFolder, setOpenFolder] = useState(null);
  const [ctx, setCtx] = useState(null); // { x, y, items }
  const [drag, setDrag] = useState(null); // { key, x, y }
  const [showPersonalize, setShowPersonalize] = useState(false);
  const [notice, setNotice] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null); // selected icon (single click)
  const lastTapRef = useRef({ key: null, time: 0 }); // for double-click-to-open detection

  const accessRoutes = useSelector((s) => s?.employee?.currentEmployeeAccess?.routes) || [];
  const notesAppOpen = useSelector((s) => s?.webtopUi?.notesAppOpen);

  // Run a built-in shell app: either a slice action (Sticky Notes panel) or
  // open a webtop window (Notepad).
  const runShellApp = (app) => {
    if (app.action === "openNotesApp") dispatch(openNotesApp());
    else if (app.openPath) dispatch(openWindow({ path: app.openPath, title: app.name }));
  };

  // Auto-dismiss the "no longer available" notice.
  useEffect(() => {
    if (!notice) return undefined;
    const id = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(id);
  }, [notice]);

  // Open a record shortcut, guarding against revoked access (Phase 17).
  const openShortcut = (sc) => {
    if (!canAccessPath(accessRoutes, sc.path)) {
      setNotice(`"${sc.label}" is no longer available or you don't have access.`);
      return;
    }
    dispatch(openWindow({ path: sc.path, title: sc.label }));
  };

  const hiddenCount = (layout?.hiddenIcons || []).length;
  const visible = menu
    .map((section, index) => ({ section, index }))
    .filter(({ section }) => !isHidden(layout, section.name));

  const openSection = (section) => {
    if (section.Subfields.length === 1) {
      const sub = section.Subfields[0];
      dispatch(openWindow({ path: subRouteOf(sub), title: sub.displayName }));
    } else {
      setOpenFolder(section);
    }
  };

  // "This PC" is always the FIRST desktop icon (top-left, index 0) for every
  // user; module icons start at index 1, then the other shell apps. These are
  // DEFAULT positions only — a user's own drag (saved per key) still wins.
  const shellDefaultIndex = (app, i) => (app.key === "this-pc" ? 0 : visible.length + i);

  const positionFor = (section, index) =>
    getIconPosition(layout, section.name) || defaultPositionForIndex(index + 1);

  // Generic drag-or-tap: dragging past the threshold commits a new position.
  // A tap that doesn't drag follows the Windows desktop convention — the first
  // click selects the icon, a second click on the same icon within
  // DOUBLE_TAP_MS opens it (onOpen).
  const startDragMove = (e, dragKey, basePos, onOpen, onCommit) => {
    if (e.button !== 0) return;
    const startX = e.clientX;
    const startY = e.clientY;
    let dragged = false;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!dragged && Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) dragged = true;
      if (dragged) {
        setDrag({ key: dragKey, x: Math.max(0, basePos.x + dx), y: Math.max(0, basePos.y + dy) });
      }
    };
    const onUp = (ev) => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      if (dragged) {
        onCommit({
          x: Math.max(0, basePos.x + (ev.clientX - startX)),
          y: Math.max(0, basePos.y + (ev.clientY - startY)),
        });
        setDrag(null);
        lastTapRef.current = { key: null, time: 0 };
        return;
      }
      // Tap (no drag): select on first click, open on a quick second click.
      const now = Date.now();
      const prev = lastTapRef.current;
      if (prev.key === dragKey && now - prev.time < DOUBLE_TAP_MS) {
        lastTapRef.current = { key: null, time: 0 };
        onOpen();
      } else {
        lastTapRef.current = { key: dragKey, time: now };
        setSelectedKey(dragKey);
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Click on the empty desktop clears the icon selection (Windows behaviour).
  const onDesktopMouseDown = (e) => {
    if (e.target === e.currentTarget) setSelectedKey(null);
  };

  const onIconMouseDown = (e, section, index) =>
    startDragMove(
      e,
      section.name,
      positionFor(section, index),
      () => openSection(section),
      (pos) => updateLayout({ iconPositions: setIconPosition(layout, section.name, pos).iconPositions })
    );

  const shortcuts = getShortcuts(layout);

  const onShortcutMouseDown = (e, sc) =>
    startDragMove(
      e,
      sc.id,
      { x: sc.x, y: sc.y },
      () => openShortcut(sc),
      (pos) => updateLayout({ recordShortcuts: setShortcutPosition(layout, sc.id, pos).recordShortcuts })
    );

  // Built-in shell apps (Sticky Notes, …) — drawn after the access icons, same
  // drag + double-click-to-open behaviour, positions persisted under app.key.
  const onShellAppMouseDown = (e, app, index) =>
    startDragMove(
      e,
      app.key,
      getIconPosition(layout, app.key) || defaultPositionForIndex(shellDefaultIndex(app, index)),
      () => runShellApp(app),
      (pos) => updateLayout({ iconPositions: setIconPosition(layout, app.key, pos).iconPositions })
    );

  const onShortcutContextMenu = (e, sc) => {
    e.preventDefault();
    e.stopPropagation();
    setCtx({
      x: e.clientX,
      y: e.clientY,
      items: [
        { label: "Open", onClick: () => openShortcut(sc) },
        { separator: true },
        {
          label: "Remove shortcut",
          danger: true,
          onClick: () => updateLayout({ recordShortcuts: removeShortcut(layout, sc.id).recordShortcuts }),
        },
      ],
    });
  };

  const onIconContextMenu = (e, section) => {
    e.preventDefault();
    e.stopPropagation();
    const pinned = isPinned(layout, section.name);
    setCtx({
      x: e.clientX,
      y: e.clientY,
      items: [
        { label: "Open", onClick: () => openSection(section) },
        { separator: true },
        {
          label: pinned ? "Unpin from taskbar" : "Pin to taskbar",
          onClick: () => updateLayout({ taskbarPins: togglePin(layout, section.name).taskbarPins }),
        },
        {
          label: "Remove from desktop",
          danger: true,
          onClick: () => updateLayout({ hiddenIcons: hideIcon(layout, section.name).hiddenIcons }),
        },
      ],
    });
  };

  const onDesktopContextMenu = (e) => {
    // Only when right-clicking empty desktop (not an icon).
    if (e.target.closest(".webtop-icon")) return;
    e.preventDefault();
    const noteX = e.clientX;
    const noteY = e.clientY;
    const items = [
      {
        label: "New sticky note",
        onClick: () => {
          const id = `note-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          updateLayout({ stickyNotes: addNote(layout, makeNote(id, noteX, noteY)).stickyNotes });
        },
      },
      { label: "Sticky Notes", onClick: () => dispatch(openNotesApp()) },
      { label: "Personalize", onClick: () => setShowPersonalize(true) },
    ];
    if (hiddenCount > 0) {
      items.push({
        label: `Restore removed apps (${hiddenCount})`,
        onClick: () => updateLayout({ hiddenIcons: [] }),
      });
    }
    items.push({ separator: true });
    items.push({ label: "Reset desktop", danger: true, onClick: () => replaceLayout(undefined) });
    setCtx({ x: e.clientX, y: e.clientY, items });
  };

  if (!menu.length) {
    return (
      <div className="webtop-desktop-empty">
        No apps available. Your access has not loaded yet.
      </div>
    );
  }

  return (
    <>
      <div className="webtop-desktop" onContextMenu={onDesktopContextMenu} onMouseDown={onDesktopMouseDown}>
        {visible.map(({ section, index }) => {
          const pos = drag && drag.key === section.name ? drag : positionFor(section, index);
          const dragging = drag && drag.key === section.name;
          return (
            <button
              key={section.name}
              type="button"
              className={`webtop-icon webtop-icon--abs${dragging ? " webtop-icon--dragging" : ""}${selectedKey === section.name ? " webtop-icon--selected" : ""}`}
              style={{ left: pos.x, top: pos.y }}
              onMouseDown={(e) => onIconMouseDown(e, section, index)}
              onContextMenu={(e) => onIconContextMenu(e, section)}
              title={section.name}
            >
              <span className="webtop-icon__glyph">
                <FontAwesomeIcon icon={getMenuIcon(section.name)} />
              </span>
              <span className="webtop-icon__label">{section.name}</span>
            </button>
          );
        })}

        {shortcuts.map((sc) => {
          const pos = drag && drag.key === sc.id ? drag : { x: sc.x, y: sc.y };
          const dragging = drag && drag.key === sc.id;
          return (
            <button
              key={sc.id}
              type="button"
              className={`webtop-icon webtop-icon--abs webtop-icon--shortcut${dragging ? " webtop-icon--dragging" : ""}${selectedKey === sc.id ? " webtop-icon--selected" : ""}`}
              style={{ left: pos.x, top: pos.y }}
              onMouseDown={(e) => onShortcutMouseDown(e, sc)}
              onContextMenu={(e) => onShortcutContextMenu(e, sc)}
              title={`${sc.label} — ${sc.path}`}
            >
              <span className="webtop-icon__glyph webtop-icon__glyph--shortcut">
                <FontAwesomeIcon icon={faFileLines} />
              </span>
              <span className="webtop-icon__label">{sc.label}</span>
            </button>
          );
        })}

        {WEBTOP_SHELL_APPS.map((app, i) => {
          const pos =
            drag && drag.key === app.key
              ? drag
              : getIconPosition(layout, app.key) || defaultPositionForIndex(shellDefaultIndex(app, i));
          const dragging = drag && drag.key === app.key;
          return (
            <button
              key={app.key}
              type="button"
              className={`webtop-icon webtop-icon--abs${dragging ? " webtop-icon--dragging" : ""}${selectedKey === app.key ? " webtop-icon--selected" : ""}`}
              style={{ left: pos.x, top: pos.y }}
              onMouseDown={(e) => onShellAppMouseDown(e, app, i)}
              title={app.name}
            >
              <span className="webtop-icon__glyph">
                <FontAwesomeIcon icon={app.icon} />
              </span>
              <span className="webtop-icon__label">{app.name}</span>
            </button>
          );
        })}
      </div>

      <FolderPopover section={openFolder} onClose={() => setOpenFolder(null)} />

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={ctx.items} onClose={() => setCtx(null)} />}

      {showPersonalize && <PersonalizePanel onClose={() => setShowPersonalize(false)} />}

      {notesAppOpen && <StickyNotesPanel onClose={() => dispatch(closeNotesApp())} />}

      {notice && (
        <div className="webtop-notice" role="alert">
          {notice}
        </div>
      )}
    </>
  );
};

export default DesktopIcons;
